"use client"

import { useState } from "react"
import { ExternalLink, Loader2 } from "lucide-react"

import { buttonVariants } from "@/components/ui/button"
import {
  trackMobileAppStoreClick,
  type MobileAppLandingInfo,
  type MobileAppStoreKey,
} from "@/lib/mobile-app"
import { cn } from "@/lib/utils"

const STORE_META: Record<
  MobileAppStoreKey,
  { label: string; urlKey: keyof MobileAppLandingInfo }
> = {
  rustore: { label: "RuStore", urlKey: "rustore_url" },
  google_play: { label: "Google Play", urlKey: "google_play_url" },
  apple: { label: "App Store", urlKey: "apple_store_url" },
}

type MobileAppStoreButtonsProps = {
  landing: MobileAppLandingInfo
  size?: "default" | "sm" | "lg"
}

export function MobileAppStoreButtons({
  landing,
  size = "sm",
}: MobileAppStoreButtonsProps) {
  const [pending, setPending] = useState<MobileAppStoreKey | null>(null)

  const stores = (Object.keys(STORE_META) as MobileAppStoreKey[]).filter(
    (key) => {
      const url = landing[STORE_META[key].urlKey]
      return typeof url === "string" && url.trim() !== ""
    },
  )

  if (stores.length === 0) return null

  async function handleClick(store: MobileAppStoreKey, fallbackUrl: string) {
    if (pending) return
    setPending(store)
    try {
      const trackedUrl = await trackMobileAppStoreClick(store)
      const href = trackedUrl ?? fallbackUrl
      window.open(href, "_blank", "noopener,noreferrer")
    } finally {
      setPending(null)
    }
  }

  return (
    <div className="flex flex-wrap gap-2">
      {stores.map((store) => {
        const meta = STORE_META[store]
        const url = String(landing[meta.urlKey])
        const isPending = pending === store
        return (
          <button
            key={store}
            type="button"
            disabled={pending !== null}
            onClick={() => void handleClick(store, url)}
            className={cn(
              buttonVariants({
                variant: "outline",
                size,
                className: "gap-1.5 whitespace-nowrap",
              }),
            )}
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            ) : (
              <ExternalLink className="h-4 w-4 shrink-0" />
            )}
            {meta.label}
          </button>
        )
      })}
    </div>
  )
}
